import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ExternalDataService, Subscription, Agent, Zone, UpdateSubscription } from './externalDataService.service';

export interface SubscriptionFilters {
  status: string;
  zone: string;
  agent: string;
}

export interface PaginatedSubscriptions {
  items: Subscription[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  filteredCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  
  // Data subjects
  private subscriptionsSubject = new BehaviorSubject<Subscription[]>([]);
  private agentsSubject = new BehaviorSubject<Agent[]>([]);
  private zonesSubject = new BehaviorSubject<Zone[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  
  // Filter subjects
  private filtersSubject = new BehaviorSubject<SubscriptionFilters>({
    status: '',
    zone: '',
    agent: ''
  });
  
  // Pagination subjects
  private currentPageSubject = new BehaviorSubject<number>(1);
  private pageSizeSubject = new BehaviorSubject<number>(10);
  
  // Public observables
  public subscriptions$ = this.subscriptionsSubject.asObservable();
  public agents$ = this.agentsSubject.asObservable();
  public zones$ = this.zonesSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public filters$ = this.filtersSubject.asObservable();
  public currentPage$ = this.currentPageSubject.asObservable();
  public pageSize$ = this.pageSizeSubject.asObservable();
  
  // Computed observables
  public filteredSubscriptions$ = combineLatest([
    this.subscriptions$,
    this.filters$,
    this.agents$,
    this.zones$
  ]).pipe(
    map(([subscriptions, filters, agents, zones]) => 
      this.filterSubscriptions(subscriptions, filters, agents, zones)
    )
  );
  
  public paginatedSubscriptions$: Observable<PaginatedSubscriptions> = combineLatest([
    this.filteredSubscriptions$,
    this.currentPage$,
    this.pageSize$
  ]).pipe(
    map(([filteredSubscriptions, currentPage, pageSize]) => 
      this.paginateSubscriptions(filteredSubscriptions, currentPage, pageSize)
    )
  );

  constructor(private externalDataService: ExternalDataService) {}

  // Initialization methods
  async initialize(): Promise<void> {
    this.loadingSubject.next(true);
    try {
      await Promise.all([
        this.loadSubscriptions(),
        this.loadAgents(),
        this.loadZones()
      ]);
    } finally {
      this.loadingSubject.next(false);
    }
  }

  // Data loading methods
  private async loadSubscriptions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.externalDataService.getAllSubscriptions(0, 1000).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (response) => {
          this.subscriptionsSubject.next(response.items);
          resolve();
        },
        error: (error) => {
          console.error('Error loading subscriptions:', error);
          reject(error);
        }
      });
    });
  }

  private async loadAgents(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.externalDataService.getAllAgents(true).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (agents) => {
          const activeAgents = agents.filter(agent => agent.isActive);
          this.agentsSubject.next(activeAgents);
          resolve();
        },
        error: (error) => {
          console.error('Error loading agents:', error);
          reject(error);
        }
      });
    });
  }

  private async loadZones(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.externalDataService.getAllZones().pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (zones) => {
          const activeZones = zones.filter(zone => !zone.disabled && !zone.deletedAt);
          this.zonesSubject.next(activeZones);
          resolve();
        },
        error: (error) => {
          console.error('Error loading zones:', error);
          reject(error);
        }
      });
    });
  }

  // Helper methods
  getAgentName(agentId: string): string {
    const agents = this.agentsSubject.value;
    const agent = agents.find(a => a.agentId === agentId);
    return agent ? agent.name : agentId;
  }

  getZoneName(zoneId: string): string {
    const zones = this.zonesSubject.value;
    const zone = zones.find(z => z.id === zoneId);
    return zone ? zone.name : zoneId;
  }

  getStatusText(status: number): string {
    switch (status) {
      case 1: return 'Active';
      case 2: return 'Expired';
      case 3: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 1: return 'status-active';
      case 2: return 'status-expired';
      case 3: return 'status-cancelled';
      default: return 'status-default';
    }
  }

  getTypeText(type: number): string {
    switch (type) {
      case 1: return 'Basic';
      case 2: return 'Standard';
      case 3: return 'Premium';
      case 4: return 'Free';
      default: return 'Unknown';
    }
  }

  getCadenceText(cadence: number): string {
    switch (cadence) {
      case 1: return 'Weekly';
      case 2: return 'Monthly';
      case 3: return 'Quarterly';
      case 4: return 'Yearly';
      default: return 'Unknown';
    }
  }

  getPaymentStatusText(status: number): string {
    switch (status) {
      case 1: return 'Paid';
      case 2: return 'Pending';
      case 3: return 'Failed';
      default: return 'Unknown';
    }
  }

  getPaymentStatusClass(status: number): string {
    switch (status) {
      case 1: return 'status-paid';
      case 2: return 'status-pending';
      case 3: return 'status-failed';
      default: return 'status-default';
    }
  }

  getPaymentMethodText(paymentMethod: number): string {
    switch (paymentMethod) {
      case 1: return 'Cash';
      case 2: return 'Bank Transfer';
      case 3: return 'UPI';
      case 4: return 'Card';
      default: return 'Unknown';
    }
  }

  // Filter methods
  private filterSubscriptions(
    subscriptions: Subscription[], 
    filters: SubscriptionFilters, 
    agents: Agent[], 
    zones: Zone[]
  ): Subscription[] {
    return subscriptions.filter(subscription => {
      // Status filter
      if (filters.status && filters.status !== '') {
        const statusText = this.getStatusText(subscription.status);
        if (statusText !== filters.status) {
          return false;
        }
      }

      // Zone filter
      if (filters.zone && filters.zone !== '') {
        const zoneName = this.getZoneName(subscription.zoneId);
        if (!zoneName.toLowerCase().includes(filters.zone.toLowerCase())) {
          return false;
        }
      }

      // Agent filter
      if (filters.agent && filters.agent !== '') {
        const agentName = this.getAgentName(subscription.agentId);
        if (!agentName.toLowerCase().includes(filters.agent.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }

  private paginateSubscriptions(
    subscriptions: Subscription[], 
    currentPage: number, 
    pageSize: number
  ): PaginatedSubscriptions {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = subscriptions.slice(startIndex, endIndex);

    return {
      items: paginatedItems,
      totalCount: subscriptions.length,
      pageIndex: currentPage - 1,
      pageSize: pageSize,
      filteredCount: subscriptions.length
    };
  }

  // Public methods for component interaction
  setFilters(filters: Partial<SubscriptionFilters>): void {
    const currentFilters = this.filtersSubject.value;
    this.filtersSubject.next({ ...currentFilters, ...filters });
    this.setCurrentPage(1); // Reset to first page when filtering
  }

  clearFilters(): void {
    this.filtersSubject.next({ status: '', zone: '', agent: '' });
    this.setCurrentPage(1);
  }

  setCurrentPage(page: number): void {
    this.currentPageSubject.next(page);
  }

  setPageSize(size: number): void {
    this.pageSizeSubject.next(size);
    this.setCurrentPage(1); // Reset to first page when changing page size
  }

  // Subscription operations
  updateSubscription(updateData: UpdateSubscription): Observable<Subscription> {
    return this.externalDataService.updateSubscription(updateData).pipe(
      map(updatedSubscription => {
        // Update the local subscription list
        const subscriptions = this.subscriptionsSubject.value;
        const index = subscriptions.findIndex(s => s.subscriptionId === updateData.subscriptionId);
        if (index !== -1) {
          subscriptions[index] = updatedSubscription;
          this.subscriptionsSubject.next([...subscriptions]);
        }
        return updatedSubscription;
      })
    );
  }

  // Shared data for forms
  getPlanTypes() {
    return [
      { value: 1, label: 'Basic' },
      { value: 2, label: 'Standard' },
      { value: 3, label: 'Premium' },
      { value: 4, label: 'Free' }
    ];
  }

  getCadenceOptions() {
    return [
      { value: 1, label: 'Weekly' },
      { value: 2, label: 'Monthly' },
      { value: 3, label: 'Quarterly' },
      { value: 4, label: 'Yearly' }
    ];
  }

  getStatusOptions() {
    return [
      { value: 1, label: 'Active' },
      { value: 2, label: 'Expired' },
      { value: 3, label: 'Cancelled' }
    ];
  }

  getPaymentMethods() {
    return [
      { value: 1, label: 'Cash' },
      { value: 2, label: 'Bank Transfer' },
      { value: 3, label: 'UPI' },
      { value: 4, label: 'Card' }
    ];
  }

  getPaymentStatusOptions() {
    return [
      { value: 1, label: 'Paid' },
      { value: 2, label: 'Pending' },
      { value: 3, label: 'Failed' }
    ];
  }

  // Create subscription method
  createSubscription(subscriptionData: any): Observable<any> {
    return this.externalDataService.createSubscription(subscriptionData).pipe(
      map(result => {
        // Refresh subscriptions after creation
        this.loadSubscriptions();
        return result;
      })
    );
  }

  // Utility methods
  refreshData(): void {
    this.initialize();
  }

  // Currency formatting utility
  formatCurrency(amount: number, currency: string = 'INR'): string {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  // Date formatting utility
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
